import {
    Box,
    Button,
    Card,
    DialogContent,
    Grid,
    Typography,
} from '@mui/material'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'
import { PaymentStatus } from 'models'
import { useTranslation } from 'react-i18next'
import usePaymentContext from '../../hook/usePaymentContext'
import styles from '../../paymentDialog.style'
import { ChargingDialog } from './ChargingDialog'
import { OrderDetail } from './OrderDetail'
import { PaymentsForm } from './PaymentsForm'
import useSplitPaymentContext, { PaymentFormModel } from './SplitPaymentContext'
import { useMemo, useState } from 'react'
import SplitPaymentDialogProvider from './SplitPaymentDialogProvider'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import PaymentDialogMobileContent from 'components/QuickSaleMobile/Dialog/PaymentDialogMobile/PaymentDialogMobileContent'
export const RECEIVE_PAYMENT_STATUS = [
    PaymentStatus.Received,
    PaymentStatus.PaidOnAccount,
    PaymentStatus.Authorised,
]
function SplitPaymentMedium() {
    const { t } = useTranslation()
    const {
        addTipsMode,
        onEnterAddTipsMode,
        onExitAddTipsMode,
        isReadyToProceedPayment,
        isAddTipsEnabled,
    } = usePaymentContext()

    const { isCharging, onClickBack } = useSplitPaymentContext()

    const onClickAddTipsButton = useMemo(
        () => (addTipsMode ? onExitAddTipsMode : onEnterAddTipsMode),
        [addTipsMode, onExitAddTipsMode, onEnterAddTipsMode],
    )

    return (
        <>
            <AppDialogTitle text={t('finish.label.split_payment')} />
            <DialogContent sx={styles.dialogContainer}>
                <Grid container direction="row" spacing={1}>
                    <Grid item xs={3}>
                        <Box sx={styles.leftPanelContainer}>
                            <Box
                                sx={{
                                    height:
                                        isAddTipsEnabled && isCharging // remove with bottom section's height
                                            ? 'calc(100% - 140px)'
                                            : 'calc(100% - 75px)',
                                    overflowY: 'auto',
                                }}
                            >
                                <OrderDetail />
                            </Box>

                            <Box
                                sx={{
                                    height:
                                        isAddTipsEnabled && isCharging
                                            ? 150
                                            : 80,
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    display: 'block',
                                    p: 2,
                                }}
                            >
                                {isAddTipsEnabled && isCharging && (
                                    <Button
                                        size="large"
                                        variant={
                                            addTipsMode
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                        color="primary"
                                        fullWidth
                                        onClick={onClickAddTipsButton}
                                        disabled={isReadyToProceedPayment}
                                        sx={styles.confirmButton}
                                    >
                                        {t('finish.button.add_tips')}
                                    </Button>
                                )}

                                <Button
                                    sx={{ mt: 2 }}
                                    size="large"
                                    color="primary"
                                    fullWidth
                                    variant="outlined"
                                    onClick={onClickBack}
                                >
                                    {t('button.back')}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={9}>
                        {isCharging ? (
                            <Box flexGrow={1} sx={styles.mainPanelContainer}>
                                <ChargingDialog />
                            </Box>
                        ) : (
                            <Card
                                variant="outlined"
                                sx={styles.mainPanelContainer}
                            >
                                <PaymentsForm />
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </>
    )
}

function ViewDetailContent({
    paymentForms,
    onBack,
}: {
    paymentForms: PaymentFormModel[]
    onBack: () => void
}) {
    return (
        <PaymentDialogMobileContent
            onBack={onBack}
            topSection={<OrderDetail />}
            finishButton={
                <Button
                    fullWidth
                    size="large"
                    disabled={paymentForms.some((a) => a.giftcard)}
                    variant="outlined"
                    color="primary"
                    onClick={onBack}
                >
                    Back
                </Button>
            }
        />
    )
}

function SplitPaymentFormContent({ onViewInfo }: { onViewInfo: () => void }) {
    const {
        onClickBack,
        splitEvenly,
        remainingAmount,
        paymentForms,
    } = useSplitPaymentContext()
    const { t } = useTranslation()

    return (
        <PaymentDialogMobileContent
            onBack={onClickBack}
            onViewInfo={onViewInfo}
            topSection={<PaymentsForm />}
            finishButton={
                <Box width={'100%'} display={'flex'} flexDirection={'column'}>
                    <Box pt={1}>
                        <Button
                            size="large"
                            fullWidth
                            disabled={paymentForms.some((a) => a.giftcard)}
                            variant="outlined"
                            color="primary"
                            onClick={splitEvenly}
                        >
                            {t('finish.button.split_evenly')}
                        </Button>
                    </Box>

                    <Box
                        pt={2}
                        display={'inline-flex'}
                        justifyContent={'center'}
                    >
                        <Typography
                            component="span"
                            textAlign={'center'}
                            sx={{
                                paddingRight: 2,
                            }}
                            variant={'h5'}
                        >
                            {t('finish.label.remaining_amount')}:{' '}
                            <MonetaryFormat
                                component="span"
                                variant={'h4'}
                                textAlign={'center'}
                                value={remainingAmount.toNumber()}
                            />
                        </Typography>
                    </Box>

                    <Box pt={2}>
                        <Button
                            fullWidth
                            size="large"
                            disabled={paymentForms.some((a) => a.giftcard)}
                            variant="outlined"
                            color="primary"
                            onClick={onClickBack}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            }
        />
    )
}

function SplitPaymentMobile() {
    const { paymentForms } = usePaymentContext()

    const { isCharging } = useSplitPaymentContext()
    const [viewDetail, setViewDetail] = useState(false)
    if (isCharging) return <ChargingDialog />
    if (viewDetail)
        return (
            <ViewDetailContent
                paymentForms={paymentForms}
                onBack={() => setViewDetail(false)}
            ></ViewDetailContent>
        )
    return (
        <SplitPaymentFormContent
            onViewInfo={() => setViewDetail(true)}
        ></SplitPaymentFormContent>
    )
}
export function SplitPaymentContainer() {
    const { isMobile } = useScreenSize()
    return (
        <SplitPaymentDialogProvider>
            {isMobile ? <SplitPaymentMobile /> : <SplitPaymentMedium />}
        </SplitPaymentDialogProvider>
    )
}
